import React from "react";
import { Tooltip } from "@mui/material";
import { APPLICATION_STATUS_CONFIG } from "./StatusControl/configs/applicationConfig";

export interface StatusHistoryEntry {
  status: string;
  entered_at: string;
  left_at?: string;
}

export interface ApplicationStatusTimelineProps {
  currentStatus: string;
  statusChangedAt?: string;
  statusHistory?: StatusHistoryEntry[];
  rejectedFromStatus?: string;
  compact?: boolean;
}

// Main happy-path track (not_matched_ai and rejected are spurs, not in the main line)
const MAIN_TRACK = [
  "applied",
  "matched_ai",
  "approved",
  "cv_sent",
  "interview_waiting",
  "interview_scheduled",
  "after_interview",
  "success",
];

const BRANCH_ANCHOR = "matched_ai";
const BRANCH_STATUS = "not_matched_ai";
const GRAY = "#d1d5db";
const FUTURE_OUTLINE = "#e5e7eb";
const FUTURE_LABEL_COLOR = "#9ca3af";

// Short labels for timeline display
const SHORT_LABELS: Record<string, string> = {
  applied: "App",
  matched_ai: "Match",
  approved: "Appr",
  cv_sent: "CV Sent",
  interview_waiting: "Int Wait",
  interview_scheduled: "Int Sched",
  after_interview: "After Int",
  success: "Success",
  not_matched_ai: "Not Match",
  rejected: "Rejected",
};

function isNextStep(statusKey: string, currentStatus: string): boolean {
  const currentIdx = MAIN_TRACK.indexOf(currentStatus);
  const statusIdx = MAIN_TRACK.indexOf(statusKey);
  return currentIdx >= 0 && statusIdx === currentIdx + 1;
}

function buildTooltip(
  statusKey: string,
  label: string,
  currentStatus: string,
  statusChangedAt: string | undefined,
  history: StatusHistoryEntry[],
): string {
  const isCurrent = statusKey === currentStatus;
  const entry = history.find((h) => h.status === statusKey);

  // Past status (has history with left_at)
  if (entry && entry.left_at) {
    const enteredDate = new Date(entry.entered_at).toLocaleDateString();
    const days = Math.round(
      (new Date(entry.left_at).getTime() -
        new Date(entry.entered_at).getTime()) /
        86400000,
    );
    return `\u2713 ${label} -- entered ${enteredDate} (${days}d)`;
  }

  // Current status (has history without left_at, or matches current)
  if (entry && !entry.left_at) {
    const enteredDate = new Date(entry.entered_at).toLocaleDateString();
    return `\u25CF ${label} -- current (since ${enteredDate})`;
  }
  if (isCurrent && statusChangedAt) {
    return `\u25CF ${label} -- current (since ${new Date(statusChangedAt).toLocaleDateString()})`;
  }
  if (isCurrent) {
    return `\u25CF ${label} -- current`;
  }

  // Next step
  if (isNextStep(statusKey, currentStatus)) {
    return `\u25CB ${label} -- next step`;
  }

  // Future status
  return `\u25CB ${label}`;
}

function resolveRejectionAnchor(
  rejectedFromStatus: string | undefined,
  statusHistory: StatusHistoryEntry[],
): string {
  if (rejectedFromStatus && MAIN_TRACK.includes(rejectedFromStatus)) {
    return rejectedFromStatus;
  }
  const reversed = [...statusHistory].reverse();
  const lastMain = reversed.find(
    (h) => MAIN_TRACK.includes(h.status) && h.status !== "rejected",
  );
  if (lastMain) return lastMain.status;
  return MAIN_TRACK[0];
}

// --- Internal: BranchSpur (not_matched_ai fork below matched_ai) ---
const BranchSpur: React.FC<{
  isActive: boolean;
  spurConnH: number;
  dotSize: number;
  statusChangedAt?: string;
  history: StatusHistoryEntry[];
}> = ({ isActive, spurConnH, dotSize, statusChangedAt, history }) => {
  const config = APPLICATION_STATUS_CONFIG[BRANCH_STATUS];
  const label = config?.label || "Not Matched (AI)";
  const shortLabel = SHORT_LABELS[BRANCH_STATUS] || label;
  const color = config?.color || "#ef4444";
  const tooltip = buildTooltip(
    BRANCH_STATUS,
    label,
    isActive ? BRANCH_STATUS : "",
    statusChangedAt,
    history,
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: 2,
      }}
    >
      <div
        style={{
          width: 2,
          height: spurConnH,
          backgroundColor: isActive ? color : FUTURE_OUTLINE,
        }}
      />
      <Tooltip title={tooltip} arrow placement="top">
        <div
          data-status={BRANCH_STATUS}
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: "50%",
            backgroundColor: isActive ? color : "transparent",
            border: `2px solid ${isActive ? color : FUTURE_OUTLINE}`,
            boxShadow: isActive ? `0 0 0 3px ${color}33` : undefined,
            cursor: "default",
          }}
        />
      </Tooltip>
      <span
        style={{
          fontSize: "10px",
          color: isActive ? color : FUTURE_LABEL_COLOR,
          fontWeight: isActive ? 600 : 400,
          whiteSpace: "nowrap",
          marginTop: 2,
          maxWidth: 64,
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: "center",
        }}
      >
        {shortLabel}
      </span>
    </div>
  );
};

// --- Internal: RejectionSpur (rejected fork below the anchor status) ---
const RejectionSpur: React.FC<{
  spurConnH: number;
  dotSize: number;
  statusChangedAt?: string;
  history: StatusHistoryEntry[];
}> = ({ spurConnH, dotSize, statusChangedAt, history }) => {
  const config = APPLICATION_STATUS_CONFIG["rejected"];
  const label = config?.label || "Rejected";
  const shortLabel = SHORT_LABELS["rejected"] || label;
  const color = config?.color || "#6b7280";
  const tooltip = buildTooltip(
    "rejected",
    label,
    "rejected",
    statusChangedAt,
    history,
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: 2,
      }}
    >
      <div
        style={{
          width: 2,
          height: spurConnH,
          backgroundColor: "#ef4444",
        }}
      />
      <Tooltip title={tooltip} arrow placement="top">
        <div
          data-status="rejected"
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: "50%",
            backgroundColor: color,
            border: `2px solid ${color}`,
            cursor: "default",
          }}
        />
      </Tooltip>
      <span
        style={{
          fontSize: "10px",
          color,
          fontWeight: 600,
          whiteSpace: "nowrap",
          marginTop: 2,
          maxWidth: 64,
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: "center",
        }}
      >
        {shortLabel}
      </span>
    </div>
  );
};

// --- Main Component ---
const ApplicationStatusTimeline: React.FC<ApplicationStatusTimelineProps> = ({
  currentStatus,
  statusChangedAt,
  statusHistory = [],
  rejectedFromStatus,
  compact = false,
}) => {
  const dotSize = compact ? 10 : 20;
  const activeDotSize = compact ? 14 : 24;
  const connH = compact ? 2 : 3;
  const spurConnH = 20;

  // Normalize unknown status to fallback
  const isUnknown =
    !MAIN_TRACK.includes(currentStatus) &&
    currentStatus !== BRANCH_STATUS &&
    currentStatus !== "rejected";
  const effectiveStatus = isUnknown ? "applied" : currentStatus;
  const isNotMatchedAI = effectiveStatus === BRANCH_STATUS;
  const isRejected = effectiveStatus === "rejected";

  // For not_matched_ai, treat as at matched_ai position for past-dot coloring
  // For rejected, all main track dots up to rejection anchor are past
  const rejectionAnchor = isRejected
    ? resolveRejectionAnchor(rejectedFromStatus, statusHistory)
    : null;

  let mainTrackIdx: number;
  if (isNotMatchedAI) {
    mainTrackIdx = MAIN_TRACK.indexOf(BRANCH_ANCHOR);
  } else if (isRejected && rejectionAnchor) {
    mainTrackIdx = MAIN_TRACK.indexOf(rejectionAnchor) + 1;
  } else {
    mainTrackIdx = MAIN_TRACK.indexOf(effectiveStatus);
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "nowrap",
          minWidth: "fit-content",
          paddingBottom: compact ? 0 : 48,
          gap: 0,
          position: "relative",
        }}
      >
        {MAIN_TRACK.map((statusKey, slotIdx) => {
          const config = APPLICATION_STATUS_CONFIG[statusKey] ?? {
            label: statusKey,
            color: GRAY,
            bgColor: "#fff",
          };
          const isActive = statusKey === effectiveStatus;
          const isPast = slotIdx < mainTrackIdx;
          const isLast = slotIdx === MAIN_TRACK.length - 1;
          const isBranchSlot = statusKey === BRANCH_ANCHOR;
          const isRejAnchor = statusKey === rejectionAnchor;

          const size = isActive ? activeDotSize : dotSize;
          const bgColor = isActive || isPast ? config.color : "transparent";
          const borderColor =
            isActive || isPast ? config.color : FUTURE_OUTLINE;

          const tooltip = buildTooltip(
            statusKey,
            config.label,
            effectiveStatus,
            statusChangedAt,
            statusHistory,
          );

          const connColor = isPast ? config.color : FUTURE_OUTLINE;

          // Label color: active = bold status color, past = status color, future = muted gray
          const labelColor =
            isActive || isPast ? config.color : FUTURE_LABEL_COLOR;
          const labelWeight = isActive ? 600 : 400;
          const shortLabel = SHORT_LABELS[statusKey] || config.label;

          return (
            <React.Fragment key={statusKey}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                {/* Main dot */}
                <Tooltip title={tooltip} arrow placement="top">
                  <div
                    data-status={statusKey}
                    style={{
                      width: size,
                      height: size,
                      borderRadius: "50%",
                      backgroundColor: bgColor,
                      border: `2px solid ${borderColor}`,
                      boxShadow: isActive
                        ? `0 0 0 3px ${config.color}33`
                        : undefined,
                      flexShrink: 0,
                      cursor: "default",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {isPast && !compact && (
                      <span
                        style={{
                          color: "white",
                          fontSize: "10px",
                          lineHeight: 1,
                          fontWeight: 700,
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                </Tooltip>

                {/* Label below every dot */}
                {!compact && (
                  <span
                    style={{
                      marginTop: 4,
                      fontSize: "10px",
                      color: labelColor,
                      fontWeight: labelWeight,
                      maxWidth: 64,
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {shortLabel}
                  </span>
                )}

                {/* Branch spur: not_matched_ai below matched_ai */}
                {isBranchSlot && !compact && (
                  <BranchSpur
                    isActive={isNotMatchedAI}
                    spurConnH={spurConnH}
                    dotSize={dotSize}
                    statusChangedAt={
                      isNotMatchedAI ? statusChangedAt : undefined
                    }
                    history={statusHistory}
                  />
                )}

                {/* Rejection spur: below the anchor status */}
                {isRejAnchor && isRejected && !compact && (
                  <RejectionSpur
                    spurConnH={spurConnH}
                    dotSize={dotSize}
                    statusChangedAt={statusChangedAt}
                    history={statusHistory}
                  />
                )}
              </div>

              {/* Horizontal connector — flex-based to stretch */}
              {!isLast && (
                <div
                  style={{
                    flex: 1,
                    minWidth: 12,
                    height: connH,
                    backgroundColor: connColor,
                    marginTop:
                      (isActive ? activeDotSize : dotSize) / 2 - connH / 2,
                    flexShrink: 1,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ApplicationStatusTimeline;
